import { useQuery, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { ExtendedProvidersTypeaheadProvider } from "#/pages/provider-finder/types/Providers"
import { CARROT_CLINIC_PROVIDER_UUID } from "#/pages/provider-finder/components/results/CarrotClinicCard"
import { trpc } from "#/lib/trpc/client"
import { trpcRetry } from "#/lib/trpc/retryPolicy"

const EXTENDED_PROVIDERS_TYPEAHEAD_QUERY_KEY = "GetExtendedProvidersTypeahead"

export function useGetExtendedProvidersTypeahead(
  providerNameSubstring: string,
  providerType?: string,
  countryCode?: string | null
): UseQueryResult<ExtendedProvidersTypeaheadProvider[], ResponseError> {
  return useQuery<ExtendedProvidersTypeaheadProvider[], ResponseError>(
    [EXTENDED_PROVIDERS_TYPEAHEAD_QUERY_KEY, providerNameSubstring, providerType, countryCode],
    async () => {
      // Carrot Plans clinic-name step uses the unscoped typeahead — matches
      // denied/pending clinics too so the flow can capture UUID + status
      // instead of a freeform name. Member-scoped keyword search on the
      // Provider Finder page uses `providerFinder.typeahead` instead.
      const result = await trpc.providerFinder.typeaheadAll.query({
        name: providerNameSubstring,
        countryCode: countryCode ?? undefined,
        providerType
      })
      // BFF emits the title-case form PF lambda used to return — pass through.
      // Drop Carrot Clinic (uuid -1) here too: Plans stores the picked uuid,
      // and that row is a Finder card, not a clinic a member works with.
      return result.data
        .filter((p) => p.id !== CARROT_CLINIC_PROVIDER_UUID)
        .map((p) => ({
          uuid: p.id,
          name: p.name,
          providerEligibilityStatus: p.providerEligibilityStatus
        }))
    },
    {
      enabled: !!providerNameSubstring,
      refetchOnWindowFocus: false,
      retry: trpcRetry(1)
    }
  )
}
